import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';

import { WebIDData } from '../../services/webids';
import { checkOnboardingStatus } from '../../hooks/useOnboardingGuard';
import { Button } from '../ui';

/**
 * @component OnboardingGuard
 * @description Componente guard que protege rutas que requieren onboarding completado
 */

interface OnboardingGuardProps {
  webId: WebIDData | null;
  loading: boolean;
  children: React.ReactNode;
  fallbackMessage?: string;
}

const OnboardingGuard: React.FC<OnboardingGuardProps> = ({
  webId,
  loading,
  children,
  fallbackMessage = 'Debes completar la configuración inicial antes de acceder a esta función.'
}) => {
  const navigate = useNavigate();
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    if (loading || !webId) return;

    const { required } = checkOnboardingStatus(webId);
    
    if (required) {
      setShouldRedirect(true);
      // Pequeño delay para mostrar el mensaje antes de redirigir
      const timer = setTimeout(() => {
        navigate(`/onboarding/${webId.id}`, { replace: true });
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [webId, loading, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!webId) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">WebID no encontrado</p>
        </div>
      </div>
    );
  }

  const { required } = checkOnboardingStatus(webId);

  if (required || shouldRedirect) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Configuración Requerida
          </h3>
          <p className="text-gray-600 mb-6">
            {fallbackMessage}
          </p>
          {shouldRedirect ? (
            <div className="flex items-center justify-center gap-2 text-primary-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Redirigiendo a configuración...</span>
            </div>
          ) : (
            <Button
              onClick={() => navigate(`/onboarding/${webId.id}`)}
              className="w-full"
            >
              Completar Configuración
            </Button>
          )}
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default OnboardingGuard;
